import { useState, useRef } from 'react';
import { Modal, ModalFooter } from './ui/Modal';
import { useToast } from './ui/Toast';
import { supportApi } from '../lib/api';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

export function SupportTicketModal({ isOpen, onClose }: SupportTicketModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setScreenshot(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large', 'Screenshot must be 2 MB or less.');
      e.target.value = '';
      return;
    }

    setScreenshot(file);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;

    setSubmitting(true);
    try {
      await supportApi.submitTicket({
        title: title.trim(),
        description: description.trim(),
        screenshot: screenshot || undefined,
      });
      toast.success('Ticket Submitted', 'Your support ticket has been sent. We\'ll get back to you soon.');
      resetForm();
      onClose();
    } catch {
      toast.error('Submission Failed', 'Could not submit your ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = title.trim().length > 0 && description.trim().length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit a Support Ticket"
      description="Describe your issue and we'll get back to you as soon as possible."
      size="lg"
    >
      <div className="space-y-4">
        {/* Title */}
        <div>
          <label htmlFor="ticket-title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Subject
          </label>
          <input
            id="ticket-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of your issue"
            maxLength={200}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={submitting}
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="ticket-description" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Description
          </label>
          <textarea
            id="ticket-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe your issue in detail..."
            maxLength={5000}
            rows={5}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
            disabled={submitting}
          />
          <p className="mt-1 text-xs text-slate-400">{description.length}/5000</p>
        </div>

        {/* Screenshot */}
        <div>
          <label htmlFor="ticket-screenshot" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Screenshot <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            ref={fileInputRef}
            id="ticket-screenshot"
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileChange}
            className="w-full text-sm text-slate-500 dark:text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-700 dark:file:text-slate-300 hover:file:bg-slate-200 dark:hover:file:bg-slate-600 file:cursor-pointer"
            disabled={submitting}
          />
          {screenshot && (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="truncate">{screenshot.name}</span>
              <button
                type="button"
                onClick={() => {
                  setScreenshot(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="ml-auto text-slate-400 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <p className="mt-1 text-xs text-slate-400">JPEG, PNG, GIF, or WebP. Max 2 MB.</p>
        </div>
      </div>

      <ModalFooter>
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting && (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </button>
      </ModalFooter>
    </Modal>
  );
}
